import React from 'react';
import { usePalette } from '../theme/theme';

export interface RadarDataPoint {
  name: string;
  value: number;
}

interface RatingRadarChartProps {
  data: RadarDataPoint[];
  size?: number;
  maxValue?: number;
}

const LABEL_PAD = 34;

const RatingRadarChart: React.FC<RatingRadarChartProps> = ({
  data,
  size = 280,
  maxValue = 100,
}) => {
  const { pal, rgba } = usePalette(); // theme step 2: SVG attributes follow the active theme
  const center = size / 2;
  const radius = (size / 2) * 0.85;
  const axes = data.length;
  const angleStep = (2 * Math.PI) / axes;
  const angles = data.map((_, i) => -Math.PI / 2 + i * angleStep);

  const toPoint = (angle: number, r: number) => ({
    x: center + r * Math.cos(angle),
    y: center + r * Math.sin(angle),
  });

  const polygonPoints = data
    .map((d, i) => {
      const r = (d.value / maxValue) * radius;
      return toPoint(angles[i], r);
    })
    .map((p) => `${p.x},${p.y}`)
    .join(' ');

  const gridLevels = [0.25, 0.5, 0.75, 1];
  const gridColor = rgba('text-mute', '0.08');
  const axisColor = rgba('text-mute', '0.14');
  // Graphite scheme (owner: no bright/rainbow colours) — a muted steel polygon over #050505, matching
  // the Rating Breakdown bars, instead of the previous bright cyan.
  const fillColor = rgba('active', '0.10');
  const strokePolygon = rgba('hex-b4beca', '0.72');
  const labelColor = pal['text-mute'];
  const valueColor = pal['text'];

  return (
    // The axis labels sit outside the plot radius; the viewBox carries a label margin so a long name
    // ("Reg.", "Market") is never clipped at the SVG edge. The chart scales down with its container.
    <svg
      width={size + 2 * LABEL_PAD}
      height={size + 2 * LABEL_PAD}
      viewBox={`${-LABEL_PAD} ${-LABEL_PAD} ${size + 2 * LABEL_PAD} ${size + 2 * LABEL_PAD}`}
      className="mx-auto block h-auto max-w-full"
      role="img"
      aria-label={`Rating radar: ${data.map((d) => `${d.name} ${d.value}`).join(', ')}`}
    >
      {/* Grid polygons */}
      {gridLevels.map((level) => {
        const r = radius * level;
        const pts = angles.map((a) => toPoint(a, r)).map((p) => `${p.x},${p.y}`).join(' ');
        return (
          <polygon
            key={level}
            points={pts}
            fill="none"
            stroke={gridColor}
            strokeWidth="1"
          />
        );
      })}
      {/* Axis lines */}
      {angles.map((angle, i) => {
        const end = toPoint(angle, radius);
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={end.x}
            y2={end.y}
            stroke={axisColor}
            strokeWidth="1"
          />
        );
      })}
      {/* Data polygon */}
      <polygon points={polygonPoints} fill={fillColor} stroke={strokePolygon} strokeWidth="2" />
      {/* Data points */}
      {data.map((d, i) => {
        const r = (d.value / maxValue) * radius;
        const pt = toPoint(angles[i], r);
        return (
          <circle key={`dot-${i}`} cx={pt.x} cy={pt.y} r="3" fill={strokePolygon} />
        );
      })}
      {/* Labels */}
      {data.map((d, i) => {
        const labelR = radius + 24;
        const pos = toPoint(angles[i], labelR);
        return (
          <g key={d.name}>
            <text
              x={pos.x}
              y={pos.y - 7}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={labelColor}
              fontSize="12"
              fontFamily="Inter, system-ui, sans-serif"
            >
              {d.name}
            </text>
            <text
              x={pos.x}
              y={pos.y + 8}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={valueColor}
              fontSize="12"
              fontFamily="JetBrains Mono, monospace"
            >
              {d.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default RatingRadarChart;
